import { useEventCallback } from '@mui/material/utils';
import { omit } from 'lodash';
import { useMemo } from 'react';

import {
  useDataMutation,
  useDataProvider,
  useUser as useUserContext,
} from '@work4all/data';

import { InputBenutzerSyncOptionAddModifyRelation } from '@work4all/models/lib/Classes/InputBenutzerSyncOptionAddModifyRelation.entity';
import { User } from '@work4all/models/lib/Classes/User.entity';
import { DataRequest } from '@work4all/models/lib/DataProvider';
import { EMode } from '@work4all/models/lib/Enums/EMode.enum';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

const USER_DATA: User = {
  id: null,
  allowToUseOwnPrivateData: null,
  privacyHideBirthday: null,
  syncOptions: [
    {
      id: null,
      mailboxId: null,
      contactsExportCustomer: null,
      contactsExportSupplier: null,
      contactsExportEmployees: null,
      contactsExportMode: null,
      contactsExportAllowed: null,
      contactsIcludePrivateData: null,
      appointmentsSync: null,
      appointmentsSyncPrivateItems: null,
      tenantCode: null,
      contactsExportCustomerAllowed: null,
      contactsExportSupplierAllowed: null,
      contactsExportEmployeesAllowed: null,
      appointmentsSyncExternalAppointments: null,
      appointmentsAllowPrivate: null,
    },
  ],
};

type Props = {
  tenantId: number;
};

const useUserDataAndMutation = () => {
  const userContext = useUserContext();

  const request = useMemo<DataRequest>(() => {
    return {
      entity: Entities.user,
      data: USER_DATA,
      filter: [
        {
          id: { $eq: userContext.benutzerCode },
        },
      ],
    };
  }, [userContext.benutzerCode]);

  const { data } = useDataProvider<User>(request);

  const user = useMemo(() => data[0] ?? {}, [data]);

  const [mutate] = useDataMutation({
    entity: Entities.user,
    mutationType: EMode.upsert,
    responseData: USER_DATA,
  });

  return [user, mutate] as const;
};

export const useUserMailboxExchangeSynchronizationData = ({
  tenantId,
}: Props) => {
  const userContext = useUserContext();
  const [user, mutateUser] = useUserDataAndMutation();
  const syncOptions = useMemo(
    () =>
      user?.syncOptions?.find(
        (syncOption) => syncOption.tenantCode === tenantId
      ) ?? {},
    [tenantId, user?.syncOptions]
  );

  const mutate = useEventCallback(
    (key: string, value: unknown, isSyncOptions: boolean) => {
      if (isSyncOptions) {
        const data: InputBenutzerSyncOptionAddModifyRelation = {
          ...omit(syncOptions, [
            '__typename',
            'contactsExportCustomerAllowed',
            'contactsExportSupplierAllowed',
            'contactsExportEmployeesAllowed',
            'contactsExportAllowed',
          ]),
          tenantCode: tenantId,
          [key]: value,
        };

        return mutateUser(
          { id: userContext.benutzerCode },
          {
            relations: {
              syncOptions: {
                addModify: [data],
              },
            },
          }
        );
      }

      return mutateUser({ id: userContext.benutzerCode, [key]: value });
    }
  );

  return { user, syncOptions, mutate };
};
